/**
 * SubmissionsTab
 * ──────────────
 * Lists all submissions for this event.
 * Uses GET /submissions?eventId=
 * Supports view detail (expand row) and delete.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, Chip, Skeleton, Collapse, Divider,
  Select, MenuItem, FormControl,
} from '@mui/material';
import {
  DeleteOutlined, AssignmentOutlined, SearchOutlined,
  KeyboardArrowDownOutlined, KeyboardArrowUpOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../../hooks/useApi';
import { PRIMARY, INPUT_SX } from '../shared';

// ── Status meta ───────────────────────────────────────────────────────────────
const STATUS_META = {
  pending:   { label: 'Pending',   color: '#d97706', bg: alpha('#d97706', 0.08) },
  confirmed: { label: 'Confirmed', color: '#00a882', bg: alpha('#00a882', 0.08) },
  rejected:  { label: 'Rejected',  color: '#d32f2f', bg: alpha('#d32f2f', 0.08) },
  waitlisted:{ label: 'Waitlisted',color: '#7c3aed', bg: alpha('#7c3aed', 0.08) },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: alpha('#6b7280', 0.08) },
};

function StatusChip({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#6b7280', bg: alpha('#6b7280', 0.08) };
  return (
    <Chip label={m.label} size="small" sx={{
      height: 20, fontSize: '0.7rem', fontWeight: 700,
      background: m.bg, color: m.color,
    }} />
  );
}

function avatarColor(email = '') {
  const hue = [...email].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return `hsl(${hue}, 60%, 48%)`;
}

// ── Expandable row ────────────────────────────────────────────────────────────
function SubmissionRow({ sub, onDelete }) {
  const [open, setOpen] = useState(false);

  const fieldEntries = Object.entries(sub.fieldData || {}).filter(([, v]) => v !== null && v !== '');

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.72rem', fontWeight: 700, bgcolor: avatarColor(sub.submitterEmail || '') }}>
              {(sub.submitterName || sub.submitterEmail || '?').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {sub.submitterName || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">{sub.submitterEmail || '—'}</Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: PRIMARY }}>{sub.referenceId}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {sub.form?.title || '—'}
          </Typography>
        </TableCell>
        <TableCell><StatusChip status={sub.status} /></TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton size="small" sx={{ color: '#c0c8d4' }}>
              {open ? <KeyboardArrowUpOutlined sx={{ fontSize: 16 }} /> : <KeyboardArrowDownOutlined sx={{ fontSize: 16 }} />}
            </IconButton>
            <Tooltip title="Delete">
              <IconButton size="small"
                onClick={e => { e.stopPropagation(); onDelete(sub._id); }}
                sx={{ color: '#c0c8d4', '&:hover': { color: '#d32f2f' } }}>
                <DeleteOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded field data */}
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2.5, background: '#fafbfc', borderBottom: '1px solid #f0f2f5' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#5a6474', mb: 1.5 }}>
                Submission Data
              </Typography>
              {fieldEntries.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No field data available.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
                  {fieldEntries.map(([key, val]) => (
                    <Box key={key} sx={{ background: '#fff', border: '1px solid #e8edf2', borderRadius: 2, px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
                        {key}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1a1a2e', wordBreak: 'break-word' }}>
                        {Array.isArray(val) ? val.join(', ') : String(val)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SubmissionsTab({ event, isActive = false }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    if (!event?._id) return;
    setLoading(true); setError('');
    try {
      const r = await api.get(`/submissions?eventId=${event._id}&limit=200`);
      setSubmissions(r.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load submissions');
    } finally { setLoading(false); }
  }, [event?._id]);

  useEffect(() => { load(); }, [load]);

  // Refetch when the user navigates back to this tab (skip initial mount)
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (isActive) load();
  }, [isActive]); // eslint-disable-line

  const handleDelete = async (id) => {
    try {
      await api.delete(`/submissions/${id}`);
      setSubmissions(prev => prev.filter(s => s._id !== id));
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const filtered = submissions.filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.submitterName  || '').toLowerCase().includes(q) ||
      (s.submitterEmail || '').toLowerCase().includes(q) ||
      (s.referenceId    || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1a1a2e' }}>
          Submissions
          {!loading && (
            <Chip label={submissions.length} size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.72rem', background: alpha(PRIMARY, 0.08), color: PRIMARY }} />
          )}
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          value={search} onChange={e => setSearch(e.target.value)}
          size="small" placeholder="Search name, email or reference ID…"
          sx={{ ...INPUT_SX, flex: 1 }}
          InputProps={{ startAdornment: <SearchOutlined sx={{ fontSize: 18, color: 'text.secondary', mr: 0.75 }} /> }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            sx={{ borderRadius: 2, background: '#f8f9fb', fontSize: '0.875rem' }}>
            <MenuItem value="all">All statuses</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box sx={{ border: '1px solid #e8edf2', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={48} sx={{ mb: 0.5, borderRadius: 2 }} />)}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 9 }}>
            <AssignmentOutlined sx={{ fontSize: 44, color: alpha(PRIMARY, 0.15), mb: 1.5 }} />
            <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {search || statusFilter !== 'all' ? 'No submissions match your filters' : 'No submissions yet'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Submitter</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Reference</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Form</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Date</TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(sub => (
                  <SubmissionRow key={sub._id} sub={sub} onDelete={handleDelete} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}